import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Clock, ArrowRight, Crown, AlertCircle, RefreshCw, Coins } from 'lucide-react';
import { useLanguage } from '../i18nContext';
import CofounderWelcomeModal from './CofounderWelcomeModal';
import { MembershipInfo, UserLevel } from '../types';
import {
  getMembershipInfo,
  formatMinutes,
  formatMinutesEn,
  getUserLevelName,
} from '../services/membership';

interface MembershipStatusProps {
  onUpgrade: () => void;
  onViewPlan?: () => void;
  refreshTrigger?: number;
}

export default function MembershipStatus({ onUpgrade, onViewPlan, refreshTrigger }: MembershipStatusProps) {
  const { t, language } = useLanguage();
  const [membershipInfo, setMembershipInfo] = useState<MembershipInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [showCofounderModal, setShowCofounderModal] = useState(false);

  useEffect(() => {
    loadMembershipInfo();
  }, []);

  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      loadMembershipInfo();
    }
  }, [refreshTrigger]);

  const loadMembershipInfo = async () => {
    try {
      setLoading(true);
      setError(null);
      const info = await getMembershipInfo();
      setMembershipInfo(info);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '加载会员信息失败';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="relative rounded-2xl bg-surface border border-surface-border p-4">
        <div className="flex items-center justify-center py-4">
          <RefreshCw size={20} className="animate-spin text-ink-muted" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="relative rounded-2xl bg-surface border border-surface-border p-4">
        <div className="flex items-center gap-2 text-red-500 text-sm">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
        <button
          onClick={loadMembershipInfo}
          className="mt-2 text-xs text-ink-secondary hover:text-brand-900 underline"
        >
          {language === 'zh' ? '重试' : 'Retry'}
        </button>
      </div>
    );
  }

  if (!membershipInfo) {
    return null;
  }

  const { userLevel, freeQuotaMinutes, freeQuotaTotal, membershipQuotaMinutes, membershipQuotaTotal, purchasedMinutes, membershipMonths, lastQuotaRefreshAt } =
    membershipInfo;

  const isCofounder = membershipInfo.isCofounder === true;
  const isPro = userLevel === 'pro';
  const isTeam = userLevel === 'team';
  const isFree = userLevel === 'free';
  const isPaid = isPro || isTeam;

  // 使用后端返回的到期时间和剩余天数
  const daysRemaining = membershipInfo.daysUntilExpire ?? 0;
  const expireAtStr = membershipInfo.membershipExpireAt;
  const formatExpiry = (dateStr: string) => {
    const date = new Date(dateStr);
    const y = date.getFullYear();
    const m = date.getMonth() + 1;
    const d = date.getDate();
    return language === 'zh' ? `${y}年${m}月${d}日` : `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  };

  // 月度配额 = 免费 + 会员
  const monthlyRemaining = freeQuotaMinutes + membershipQuotaMinutes;
  const monthlyCap = freeQuotaTotal + (membershipQuotaTotal > 0 ? membershipQuotaTotal : 0);
  // 进度条百分比（基于月度配额）
  const usagePercent = isTeam ? 100 : (monthlyCap > 0 ? Math.min(100, Math.round((monthlyRemaining / monthlyCap) * 100)) : 0);

  // 格式化时长显示
  const formatQuota = (minutes: number) => {
    return language === 'zh' ? formatMinutes(minutes) : formatMinutesEn(minutes);
  };

  return (
    <div
      className={`relative rounded-[16px] border overflow-hidden ${isPaid
        ? 'bg-gradient-to-br from-white to-prestige-100 border-prestige-border shadow-prestige ring-1 ring-prestige-border/30'
        : 'bg-surface border-surface-border shadow-sm transition-colors'
        }`}
    >
      {isPaid ? (
        <>
          <div className="absolute top-[-30px] right-[-30px] w-40 h-40 bg-prestige-200/40 blur-[40px] rounded-full pointer-events-none"></div>
          <div className="absolute inset-0 bg-noise opacity-[0.04] pointer-events-none"></div>
        </>
      ) : (
        <>
          <div className="absolute top-[-30px] right-[-30px] w-40 h-40 bg-ink-muted/5 blur-[40px] rounded-full pointer-events-none"></div>
          <div className="absolute inset-0 bg-noise opacity-[0.02] pointer-events-none"></div>
        </>
      )}

      <div className="px-3.5 pt-3.5 pb-3 relative z-10 flex flex-col gap-2.5">
        {/* 头部：等级标识与标签 */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <div className="font-serif font-black text-[20px] tracking-tight flex items-center">
              {isTeam ? (
                <span className="text-prestige-500 drop-shadow-sm flex items-center gap-1.5 cursor-default">
                  <Crown size={16} fill="currentColor" />
                  Team <span className="italic font-bold ml-0.5 opacity-80">Plan</span>
                </span>
              ) : isPro ? (
                <span className="text-prestige-500 drop-shadow-sm cursor-default">
                  Pro <span className="italic font-bold ml-0.5 opacity-80">Plan</span>
                </span>
              ) : (
                <div className="text-brand-900 cursor-default">
                  <span>Free</span> <span className="italic text-brand-900/60 font-bold ml-0">Plan</span>
                </div>
              )}
            </div>

            {isCofounder && (
              <button
                type="button"
                className="cofounder-badge ml-auto relative flex items-center gap-1.5 pl-2.5 pr-3 py-1.5 rounded-lg border border-amber-300/70 overflow-hidden cursor-pointer hover:ring-1 hover:ring-amber-400 transition-all active:scale-95"
                onClick={() => setShowCofounderModal(true)}
                aria-label="View Co-founder benefits"
              >
                {/* Floating bubble background */}
                <div className="absolute top-0 left-1 w-7 h-7 rounded-full bg-amber-400/30 blur-[6px] cofounder-bubble-1 pointer-events-none"></div>
                <div className="absolute bottom-0 right-1 w-6 h-6 rounded-full bg-yellow-500/25 blur-[6px] cofounder-bubble-2 pointer-events-none"></div>
                {/* Stars cluster icon */}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="shrink-0 relative z-10 text-amber-700" style={{filter: 'drop-shadow(0 0 2px rgba(180,120,20,0.4))'}}>
                  <path d="M12 5l1.8 5h5.2l-4.2 3 1.6 4.8L12 15l-4.4 2.8 1.6-4.8L5 10h5.2L12 5z" fill="currentColor" opacity="0.85"/>
                  <path d="M21.5 3.5l.5 1.3h1.3l-1 .8.4 1.2-1.2-.7-1.2.7.4-1.2-1-.8H21l.5-1.3z" fill="currentColor" opacity="0.5"/>
                  <path d="M3.5 19l.5 1.3h1.3l-1 .8.4 1.2-1.2-.7-1.2.7.4-1.2-1-.8H3l.5-1.3z" fill="currentColor" opacity="0.4"/>
                </svg>
                {/* Sparkle dots */}
                <div className="cofounder-star-1 absolute top-1 right-2 w-[3px] h-[3px] rounded-full bg-amber-500/60"></div>
                <div className="cofounder-star-2 absolute bottom-1.5 right-4 w-[2px] h-[2px] rounded-full bg-yellow-600/50"></div>
                <div className="cofounder-star-3 absolute top-2 left-1.5 w-[2px] h-[2px] rounded-full bg-amber-600/40"></div>
                {/* Text */}
                <span className="relative z-10 text-[9.5px] font-black uppercase tracking-[0.15em] bg-gradient-to-r from-amber-800 via-yellow-700 to-amber-800 bg-clip-text text-transparent leading-none">
                  Co-founder
                </span>
              </button>
            )}
          </div>

        {/* 到期时间 */}
        {isPaid && expireAtStr && (
          <div className="text-[11px] font-medium tracking-wide text-ink-secondary flex items-center whitespace-nowrap overflow-hidden text-ellipsis">
            <Clock size={10} className="text-ink-muted shrink-0 mr-1" strokeWidth={2.5} />
            <span className="shrink-0">{t.paymentModal.expiresLabel}</span>
            <span className="text-ink-900 font-bold shrink-0 mx-0.5">{formatExpiry(expireAtStr)}</span>
            <span className="text-ink-muted/80 shrink-0">({daysRemaining}{t.paymentModal.daysLeft})</span>
          </div>
        )}
        </div>

        {/* 时长信息（双轨共用容器） */}
        <div className="space-y-2 p-2.5 rounded-xl bg-surface-light border border-surface-border/50">
          
          {/* 月度配额跑道 */}
          <div className="space-y-1.5">
            <div className="text-[11px] font-bold flex justify-between items-baseline tracking-wider text-ink-secondary">
              <span className="uppercase flex items-center gap-1.5">
                <Clock size={11} strokeWidth={2.5} className={isPaid ? 'text-prestige-400' : 'text-brand-500'} />
                {t.paymentModal.memberMonthly}
              </span>
              {isTeam ? (
                <span className="text-prestige-500 font-black">
                  {t.paymentModal.memberUnlimited}
                </span>
              ) : (
                <div className={`font-mono text-xs flex items-baseline gap-0.5 ${isPaid ? 'text-prestige-600' : 'text-brand-900'}`}>
                  <span className="font-black text-[13px]">{formatQuota(monthlyRemaining)}</span>
                  <span className="text-[10px] text-ink-muted font-semibold">/{formatQuota(monthlyCap)}</span>
                </div>
              )}
            </div>

            {!isTeam && (
              <div
                className={`h-[4px] w-full rounded-full overflow-hidden ${isPaid
                  ? 'bg-prestige-100'
                  : 'bg-surface-border'
                  }`}
              >
                <div
                  className={`h-full rounded-full transition-all duration-1000 ease-out ${isPaid
                    ? 'bg-prestige-400'
                    : 'bg-brand-500'
                    }`}
                  style={{ width: `${usagePercent}%` }}
                ></div>
              </div>
            )}
          </div>

          <div className="w-full h-px border-t border-dashed border-surface-divider/60"></div>

          {/* 永久配额跑道（加购时长） */}
          <div className="space-y-1.5">
            <div className="text-[11px] font-bold flex justify-between items-baseline tracking-wider text-ink-secondary">
              <span className="uppercase flex items-center gap-1.5">
                <Coins size={11} strokeWidth={2.5} className={isPaid ? 'text-prestige-400' : 'text-brand-500'} />
                {t.paymentModal.memberPermanent}
              </span>
              <div className={`font-mono text-xs flex items-baseline gap-0.5 ${isPaid ? 'text-prestige-600' : 'text-brand-900'}`}>
                <span className="font-black text-[13px]">{formatQuota(purchasedMinutes || 0)}</span>
              </div>
            </div>
            <div
              className={`h-[4px] w-full rounded-full overflow-hidden ${isPaid
                ? 'bg-prestige-100'
                : 'bg-surface-border'
                }`}
            >
              <div
                className={`h-full rounded-full transition-all duration-1000 ease-out ${isPaid
                  ? 'bg-prestige-400'
                  : 'bg-brand-500'
                  }`}
                style={{ width: purchasedMinutes > 0 ? '100%' : '0%' }}
              ></div>
            </div>
          </div>
        </div>
        {/* 底部操作区 */}
        <div>
          <button
            onClick={onViewPlan}
            className={`w-full py-2 px-2 rounded-lg text-xs font-bold tracking-wider flex items-center justify-center gap-1 transition-all duration-300 group/btn border ${isPaid
              ? 'bg-white hover:bg-prestige-50 text-prestige-600 border-prestige-200/80 hover:border-prestige-300'
              : 'bg-white hover:bg-surface-light text-brand-700 border-surface-border/60 hover:border-surface-border'
              }`}
          >
            {t.paymentModal.manage}
            <ArrowRight size={11} strokeWidth={2.5} className="opacity-80 transition-transform duration-300 group-hover/btn:translate-x-0.5" />
          </button>
        </div>
      </div>

      {showCofounderModal && createPortal(
        <CofounderWelcomeModal
          onClose={() => setShowCofounderModal(false)}
          onViewPricing={() => {
            setShowCofounderModal(false);
            if (onViewPlan) onViewPlan();
          }}
        />,
        document.body
      )}
    </div>
  );
}
